import { apiClient } from "@/api/api-client";
import { Button } from "@/components/core/buttons/button";
import ImageSelect from "@/components/core/forms/image-select";
import type { User } from "@/features/dashboard/types/user.ts";
import { useAuth } from "@/hooks/use-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Eye, EyeOff, Fingerprint, Key, Lock, Mail, Phone, Shield, Smartphone, User as UserIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/user-profile")({
	component: RouteComponent,
});

// Types

interface BasicInfoForm {
	first_name: string;
	last_name: string;
	email: string;
	contact_number: string;
}

interface PasswordForm {
	current_password: string;
	new_password: string;
	new_password_confirmation: string;
}

interface Passkey {
	id: string;
	name: string;
	created_at: string;
	last_used: string;
	device_type: "mobile" | "desktop" | "security_key";
}

type TabId = "basic" | "password" | "passkeys";

interface Tab {
	id: TabId;
	label: string;
	icon: React.ComponentType<{ className?: string }>;
}

function RouteComponent() {
	const { user, isLoading } = useAuth();
	const [activeTab, setActiveTab] = useState<TabId>("basic");

	// Handle loading state
	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
			</div>
		);
	}

	if (!user) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<h2 className="text-2xl font-semibold mb-4">Not Authenticated</h2>
					<p className="text-gray-600">Please log in to view your profile.</p>
				</div>
			</div>
		);
	}
	// const baseUrl = "http://localhost:8000";

	// const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);

	// useEffect(() => {
	// 	if (user?.profile_picture) {
	// 		setProfilePictureUrl(baseUrl + user.profile_picture);
	// 	}
	// }, [user?.profile_picture]);

	// Fetch current user profile
	// const { data: user, isLoading } = useQuery({
	//   queryKey: ['currentUser'],
	//   queryFn: async () => {
	//     const response = await apiClient.get('/user/profile');
	//     return response.data;
	//   }
	// });

	// Fetch passkeys
	const { data: passkeys = [] } = useQuery({
		queryKey: ["passkeys"],
		queryFn: async () => {
			const response = await apiClient.get("/user/passkeys");
			return response.data;
		},
	});

	const tabs: Tab[] = [
		{ id: "basic", label: "Basic Information", icon: UserIcon },
		{ id: "password", label: "Password & Security", icon: Lock },
		{ id: "passkeys", label: "Passkeys", icon: Key },
	];

	return (
		<div className="container mx-auto px-4 py-6 max-w-6xl">
			{/* Header */}
			<div className="mb-8">
				<h1 className="text-2xl font-bold text-foreground-dark">My Profile</h1>
				<p className="text-base text-foreground/70 mt-1">Manage your personal information and security settings</p>
			</div>

			{/* Tab Navigation */}
			<div className="bg-white rounded-lg shadow mb-6">
				<nav className="flex border-b">
					{tabs.map((tab) => {
						const Icon = tab.icon;
						return (
							<button
								type="button"
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`
                  flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all
                  border-b-2 ${
										activeTab === tab.id ? "border-primary-600 text-primary-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
									}
                `}
							>
								<Icon className="w-4 h-4" />
								{tab.label}
							</button>
						);
					})}
				</nav>
			</div>

			{/* Tab Content */}
			<div className="bg-white rounded-lg shadow p-6">
				{activeTab === "basic" && <BasicInformationTab user={user} />}
				{activeTab === "password" && <PasswordSecurityTab />}
				{activeTab === "passkeys" && <PasskeysTab passkeys={passkeys} />}
			</div>
		</div>
	);
}

// Basic Information Tab Component
function BasicInformationTab({ user }: { user: User }) {
	const queryClient = useQueryClient();
	const [showPasswordField, setShowPasswordField] = useState(false);
	const baseUrl = "http://localhost:8000";

	const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);

	useEffect(() => {
		if (user?.profile_picture) {
			setProfilePictureUrl(baseUrl + user.profile_picture);
		}
	}, [user?.profile_picture]);

	const { fetchProfile } = useAuth();

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
	} = useForm<BasicInfoForm>({
		defaultValues: {
			first_name: user?.first_name || "",
			last_name: user?.last_name || "",
			email: user?.email || "",
			contact_number: user?.contact_number || "",
		},
	});

	const updateProfile = useMutation({
		mutationFn: async (data: BasicInfoForm) => {
			const response = await apiClient.put(`/user/profile/${user?.id || ""}`, data);
			return response.data;
		},
		onSuccess: async () => {
			toast.success("Profile updated successfully");
			queryClient.invalidateQueries({ queryKey: ["currentUser"] });
			await fetchProfile();
		},
		onError: (error: any) => {
			toast.error(error.response?.data?.message || "Failed to update profile");
		},
	});

	const handleImageCrop = async (croppedImage: string, fileName = "profile.png") => {
		try {
			const response = await fetch(croppedImage);
			const blob = await response.blob();

			const formData = new FormData();
			formData.append("profile_picture", blob, fileName);

			const uploadResponse = await apiClient.post(`/user-approvals/${user?.id}/avatar`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			setProfilePictureUrl(uploadResponse.data.profile_picture_url);
			queryClient.invalidateQueries({ queryKey: ["currentUser"] });
			toast.success("Profile picture updated");
		} catch (error) {
			toast.error("Failed to update profile picture");
		}
	};

	const handleImageRemove = async () => {
		try {
			await apiClient.delete("/user/avatar");
			setProfilePictureUrl(null);
			queryClient.invalidateQueries({ queryKey: ["currentUser"] });
			toast.success("Profile picture removed");
		} catch (error) {
			toast.error("Failed to remove profile picture");
		}
	};

	const getUserInitials = () => {
		const first = user?.first_name?.[0] || "";
		const last = user?.last_name?.[0] || "";
		return (first + last).toUpperCase() || "U";
	};

	const onSubmit = (data: BasicInfoForm) => {
		updateProfile.mutate(data);
	};

	return (
		<div className="space-y-6">
			{/* Profile Picture Section */}
			<div className="border-b pb-6">
				<h3 className="text-lg font-semibold mb-4">Profile Picture</h3>
				<ImageSelect
					avatar={user.profile_picture || profilePictureUrl}
					initials={getUserInitials()}
					onCrop={handleImageCrop}
					onImageRemove={handleImageRemove}
					hasLabel={false}
					size={90}
				/>
			</div>

			{/* Basic Information Form */}
			<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
				<div>
					<h3 className="text-lg font-semibold mb-4">Personal Information</h3>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
								First Name
							</label>
							<input
								id="first_name"
								{...register("first_name", { required: "First name is required" })}
								type="text"
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
							/>
							{errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name.message}</p>}
						</div>

						<div>
							<label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
								Last Name
							</label>
							<input
								id="last_name"
								{...register("last_name", { required: "Last name is required" })}
								type="text"
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
							/>
							{errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name.message}</p>}
						</div>

						<div>
							<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
								Email Address
							</label>
							<div className="relative">
								<Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
								<input
									id="email"
									{...register("email", {
										required: "Email is required",
										pattern: {
											value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
											message: "Invalid email address",
										},
									})}
									type="email"
									className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
								/>
							</div>
							{errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
						</div>

						<div>
							<label htmlFor="contact_number" className="block text-sm font-medium text-gray-700 mb-1">
								Phone Number
							</label>
							<div className="relative">
								<Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
								<input
									id="contact_number"
									{...register("contact_number")}
									type="tel"
									className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Submit Button */}
				<div className="flex justify-end pt-4 border-t">
					<Button type="submit" disabled={!isDirty || updateProfile.isPending}>
						{updateProfile.isPending ? "Saving..." : "Save Changes"}
					</Button>
				</div>
			</form>
		</div>
	);
}

// Password & Security Tab Component
function PasswordSecurityTab() {
	const user = useAuth().user;
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const {
		register,
		handleSubmit,
		watch,
		reset,
		formState: { errors },
	} = useForm<PasswordForm>();
	const newPassword = watch("new_password");

	const updatePassword = useMutation({
		mutationFn: async (data: PasswordForm) => {
			const response = await apiClient.put(`/user/password/${user?.id || ""}`, data);
			return response.data;
		},
		onSuccess: () => {
			toast.success("Password updated successfully");
			reset();
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to update password");
		},
	});

	const onSubmit = (data: PasswordForm) => {
		updatePassword.mutate(data);
	};

	// Password strength indicator
	const getPasswordStrength = (password: string): { strength: string; color: string; percent: number } => {
		if (!password) return { strength: "", color: "", percent: 0 };

		let strength = 0;
		if (password.length >= 8) strength++;
		if (password.length >= 12) strength++;
		if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
		if (/\d/.test(password)) strength++;
		if (/[^a-zA-Z\d]/.test(password)) strength++;

		if (strength <= 2) return { strength: "Weak", color: "bg-red-500", percent: 33 };
		if (strength <= 4) return { strength: "Medium", color: "bg-yellow-500", percent: 66 };
		return { strength: "Strong", color: "bg-green-500", percent: 100 };
	};

	const passwordStrength = getPasswordStrength(newPassword);

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-semibold mb-2">Change Password</h3>
				<p className="text-sm text-gray-600 mb-6">Ensure your account is using a long, random password to stay secure.</p>
			</div>

			<form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-md">
				{/* Current Password */}
				<div>
					<label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">
						Current Password
					</label>
					<div className="relative">
						<input
							id="current_password"
							{...register("current_password", { required: "Current password is required" })}
							type={showCurrentPassword ? "text" : "password"}
							className="w-full pr-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
						/>
						<button
							type="button"
							onClick={() => setShowCurrentPassword(!showCurrentPassword)}
							className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
						>
							{showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
						</button>
					</div>
					{errors.current_password && <p className="text-red-500 text-xs mt-1">{errors.current_password.message}</p>}
				</div>

				{/* New Password */}
				<div>
					<label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
						New Password
					</label>
					<div className="relative">
						<input
							id="new_password"
							{...register("new_password", {
								required: "New password is required",
								minLength: {
									value: 8,
									message: "Password must be at least 8 characters",
								},
								pattern: {
									value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
									message: "Password must contain uppercase, lowercase, and number",
								},
							})}
							type={showNewPassword ? "text" : "password"}
							className="w-full pr-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
						/>
						<button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
							{showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
						</button>
					</div>
					{errors.new_password && <p className="text-red-500 text-xs mt-1">{errors.new_password.message}</p>}

					{/* Password Strength Indicator */}
					{newPassword && (
						<div className="mt-2">
							<div className="flex items-center justify-between mb-1">
								<span className="text-xs text-gray-600">Password strength</span>
								<span className={`text-xs font-medium ${passwordStrength.color.replace("bg-", "text-")}`}>{passwordStrength.strength}</span>
							</div>
							<div className="h-2 bg-gray-200 rounded-full overflow-hidden">
								<div className={`h-full transition-all ${passwordStrength.color}`} style={{ width: `${passwordStrength.percent}%` }} />
							</div>
						</div>
					)}
				</div>

				{/* Confirm New Password */}
				<div>
					<label htmlFor="new_password_confirmation" className="block text-sm font-medium text-gray-700 mb-1">
						Confirm New Password
					</label>
					<div className="relative">
						<input
							id="new_password_confirmation"
							{...register("new_password_confirmation", {
								required: "Please confirm your password",
								validate: (value) => value === newPassword || "Passwords do not match",
							})}
							type={showConfirmPassword ? "text" : "password"}
							className="w-full pr-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
						/>
						<button
							type="button"
							onClick={() => setShowConfirmPassword(!showConfirmPassword)}
							className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
						>
							{showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
						</button>
					</div>
					{errors.new_password_confirmation && <p className="text-red-500 text-xs mt-1">{errors.new_password_confirmation.message}</p>}
				</div>

				{/* Password Requirements */}
				<div className="bg-gray-50 p-4 rounded-md">
					<p className="text-sm font-medium text-gray-700 mb-2">Password requirements:</p>
					<ul className="text-xs text-gray-600 space-y-1">
						<li className="flex items-center gap-2">
							<Check className={`h-3 w-3 ${newPassword?.length >= 8 ? "text-green-500" : "text-gray-400"}`} />
							Minimum 8 characters
						</li>
						<li className="flex items-center gap-2">
							<Check className={`h-3 w-3 ${/[A-Z]/.test(newPassword || "") ? "text-green-500" : "text-gray-400"}`} />
							One uppercase letter
						</li>
						<li className="flex items-center gap-2">
							<Check className={`h-3 w-3 ${/[a-z]/.test(newPassword || "") ? "text-green-500" : "text-gray-400"}`} />
							One lowercase letter
						</li>
						<li className="flex items-center gap-2">
							<Check className={`h-3 w-3 ${/\d/.test(newPassword || "") ? "text-green-500" : "text-gray-400"}`} />
							One number
						</li>
					</ul>
				</div>

				<div className="flex justify-end pt-4">
					<Button type="submit" disabled={updatePassword.isPending}>
						{updatePassword.isPending ? "Updating..." : "Update Password"}
					</Button>
				</div>
			</form>
		</div>
	);
}

// Passkeys Tab Component
function PasskeysTab({ passkeys }: { passkeys: Passkey[] }) {
	const queryClient = useQueryClient();
	const [isAddingPasskey, setIsAddingPasskey] = useState(false);

	const addPasskey = useMutation({
		mutationFn: async () => {
			// Check if WebAuthn is supported
			if (!window.PublicKeyCredential) {
				throw new Error("WebAuthn is not supported in this browser");
			}

			// Get registration options from server
			const optionsResponse = await apiClient.get("/user/passkeys/register/options");
			const options = optionsResponse.data;

			// Create credentials
			const credential = await navigator.credentials.create({
				publicKey: options,
			});

			// Send to server
			const response = await apiClient.post("/user/passkeys/register", {
				credential: credential,
			});

			return response.data;
		},
		onSuccess: () => {
			toast.success("Passkey added successfully");
			queryClient.invalidateQueries({ queryKey: ["passkeys"] });
			setIsAddingPasskey(false);
		},
		onError: (error: { message?: string }) => {
			toast.error(error.message || "Failed to add passkey");
			setIsAddingPasskey(false);
		},
	});

	const deletePasskey = useMutation({
		mutationFn: async (id: string) => {
			await apiClient.delete(`/user/passkeys/${id}`);
		},
		onSuccess: () => {
			toast.success("Passkey removed successfully");
			queryClient.invalidateQueries({ queryKey: ["passkeys"] });
		},
		onError: () => {
			toast.error("Failed to remove passkey");
		},
	});

	const handleAddPasskey = async () => {
		setIsAddingPasskey(true);
		addPasskey.mutate();
	};

	const getDeviceIcon = (deviceType: string) => {
		switch (deviceType) {
			case "mobile":
				return <Smartphone className="w-5 h-5" />;
			case "security_key":
				return <Key className="w-5 h-5" />;
			default:
				return <Fingerprint className="w-5 h-5" />;
		}
	};

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-semibold mb-2">Passkeys</h3>
				<p className="text-sm text-gray-600 mb-6">
					Passkeys are a safer and easier replacement for passwords. They let you sign in with your fingerprint, face, or screen lock.
				</p>
			</div>

			{/* Benefits Section */}
			<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
				<h4 className="font-medium text-blue-900 mb-2">Why use passkeys?</h4>
				<ul className="text-sm text-blue-800 space-y-1">
					<li className="flex items-start gap-2">
						<Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
						<span>Faster sign-in with just your fingerprint or face</span>
					</li>
					<li className="flex items-start gap-2">
						<Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
						<span>More secure than passwords - can't be guessed or stolen</span>
					</li>
					<li className="flex items-start gap-2">
						<Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
						<span>Works across all your devices automatically</span>
					</li>
				</ul>
			</div>

			{/* Passkeys List */}
			{passkeys.length > 0 ? (
				<div className="space-y-3">
					<h4 className="font-medium text-gray-700">Your Passkeys</h4>
					{passkeys.map((passkey) => (
						<div key={passkey.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
							<div className="flex items-center gap-4">
								<div className="p-2 bg-gray-100 rounded-lg">{getDeviceIcon(passkey.device_type)}</div>
								<div>
									<p className="font-medium">{passkey.name}</p>
									<p className="text-sm text-gray-600">
										Added on {new Date(passkey.created_at).toLocaleDateString()}
										{passkey.last_used && ` • Last used ${new Date(passkey.last_used).toLocaleDateString()}`}
									</p>
								</div>
							</div>
							<Button variant="outline" size="sm" onClick={() => deletePasskey.mutate(passkey.id)} disabled={deletePasskey.isPending}>
								<X className="w-4 h-4" />
							</Button>
						</div>
					))}
				</div>
			) : (
				<div className="text-center py-8 bg-gray-50 rounded-lg">
					<Key className="w-12 h-12 mx-auto text-gray-400 mb-3" />
					<p className="text-gray-600 mb-1">No passkeys added yet</p>
					<p className="text-sm text-gray-500">Add a passkey to enable passwordless sign-in</p>
				</div>
			)}

			{/* Add Passkey Button */}
			<div className="flex justify-start pt-4">
				<Button onClick={handleAddPasskey} disabled={isAddingPasskey || addPasskey.isPending}>
					<Key className="w-4 h-4 mr-2" />
					{isAddingPasskey ? "Setting up..." : "Add a Passkey"}
				</Button>
			</div>

			{/* Browser Support Note */}
			<div className="text-xs text-gray-500 pt-4 border-t">
				<p>* Passkeys are supported in most modern browsers including Chrome, Safari, Edge, and Firefox.</p>
				<p>* You can use passkeys from your phone, computer, or security key.</p>
			</div>
		</div>
	);
}
